import { RFQ_QUOTE_VARIANTS as SHARED_RFQ_QUOTE_VARIANTS } from '@misumi/shared'
import type { ChipTone } from '../../components/ui/Chip'
import type { components } from '../../lib/api/schema.gen'
import { displayNameFor, findSourceableById } from '../../lib/fixtures/sourceable-lookup'
import { addAmounts, multiplyAmount } from '../cart/cart-money'
import { applyVariantRatio, committedDateForVariant } from './variant-pricing'

/** The RFQ read shape returned by `GET /rfq` (list) and `GET /rfq/{id}` — the
 * frozen `Rfq` value object PLUS the read-surfaced projections (`shipMethod`
 * etc. per ADR-0053 §6, and `bundled` per ADR-0055/UID-134). We deliberately
 * type the vertical's `Rfq` as this read shape (a superset of the frozen `Rfq`)
 * because that is what the API actually sends — so an additive read projection
 * like `bundled` is available to the list card without a frozen-schema change. */
export type Rfq = components['schemas']['RfqView']
export type RfqStatus = components['schemas']['RfqStatus']
export type Quote = components['schemas']['Quote']
export type QuoteVariant = components['schemas']['QuoteVariant']
export type RfqLineItem = components['schemas']['LineItem']
/** UID-171 PART 2 (ADR-0063) — an `RfqView` line, i.e. a `LineItem` PLUS the
 * requirements snapshot (`requirements: string | null`, always present on
 * every wire line — see the generated schema doc comment). Exported
 * separately from `RfqLineItem` because `Rfq['lines']`'s inferred element
 * type (an intersection of the frozen `Rfq.lines: LineItem[]` and
 * `RfqView.lines?: RfqLineView[]`) does not expose `requirements` through
 * plain property access — components that need it read `rfq.lines` typed
 * as `readonly RfqLineView[]` instead. */
export type RfqLineView = components['schemas']['RfqLineView']
/** UID-267/UID-528 (ADR-0092) — one supporting-file reference for an RFQ
 * line (`RfqLineView.files[]`), rendered as a per-part chip on the RFQ
 * detail screen (design L1763/L3571). Still carries no presigned URL/object
 * key on the wire (see the generated schema doc comment) — the CLIENT
 * resolves one on click via the existing `/parts/{partId}/attachments/
 * {attachmentId}/download` seam (`RfqCard.tsx`'s `RfqLineFileChip`), keyed
 * by `(line.sourceable.id, file.id)`. `id` is optional; a line's `name`/`id`
 * are now REAL Parts Library attachment identity (no more demo hash-pool). */
export type RfqLineFileView = components['schemas']['RfqLineFileView']
/** UID-289 (ADR-0093 §C2/§C5) — the per-line DISPLAY identity projection
 * (`RfqLineView.part`): human name/partNumber/process/thumbnailRef. Not a
 * named schema on the wire (an inline object on `RfqLineView`), so this
 * alias is derived rather than a direct `components['schemas'][...]` lookup
 * — mirrors how `RfqLineFileView` is exposed for `files`. Present-only-when-
 * non-empty: a line with no seeded part data (a catalog/id-less line) omits
 * `part` entirely, so every consumer must treat this as optional. */
export type RfqLinePartView = NonNullable<RfqLineView['part']>
export type Quantity = components['schemas']['Quantity']
export type SharedSession = components['schemas']['SharedSession']
/** UID-171 (ADR-0062) — the BOUNDED read projection of a requested add-on
 * service surfaced on `RfqView.requestedServices` (type/scope/sourceableIds; the
 * free-text `description` is deliberately absent from this shape — ADR-0042
 * preserved). The read counterpart of the write-only `RfqRequestedService`. */
export type RfqRequestedServiceView = components['schemas']['RfqRequestedServiceView']

// ---------------------------------------------------------------------------
// screens.md §14 — Draft / Requested / Quoted / Expired tabs
// ---------------------------------------------------------------------------

/** `'draft'` is UID-113's client-bundle-driven tab (ADR-0049) — NOT a server
 * RFQ status bucket. See `RfqServerTabId` below for the subset the server
 * bucketing actually ranges over. */
export type RfqTabId = 'draft' | 'requested' | 'quoted' | 'expired'

/** The subset that SERVER RFQs bucket into. `tabForStatus`/`groupByTab`
 * range over this, so a server `draft`-status RFQ (the transient two-call
 * submit sequence's intermediate state, DM§33.1) can never be typed into
 * the client Draft tab. */
export type RfqServerTabId = Exclude<RfqTabId, 'draft'>

export const RFQ_TABS: readonly { readonly id: RfqTabId; readonly label: string }[] = [
  { id: 'draft', label: 'Draft' }, // ADR-0101 — always shown, see visibleRfqTabs
  { id: 'requested', label: 'Requested' },
  { id: 'quoted', label: 'Quoted' },
  { id: 'expired', label: 'Expired' },
]

/**
 * Maps the frozen `RfqStatus` (DM§33.1) onto screens.md §14's server tabs.
 * `draft` is excluded (null) — this screen lists RFQs the customer has
 * already submitted; a draft belongs to the UID-56 review/build flow, not
 * here. `quoting` buckets under "Requested" — customer-facing it's still
 * just "being sourced," whether the automated path or a human is behind it
 * (the auto_quoting/manual_takeover split, UID-52/53's `quotingSubState`, is
 * CPQ/ops-internal and never surfaces on this screen). `accepted`/`declined`
 * bucket under "Quoted": the spec has no dedicated tab for an already-decided
 * RFQ, and grouping it with its quote keeps the customer from losing track
 * of it rather than inventing a fourth tab the mock doesn't show.
 *
 * Body UNCHANGED by UID-113/ADR-0049 — only the return type narrows to
 * `RfqServerTabId`. Existing behavior (draft status -> null) stays verbatim.
 */
export function tabForStatus(status: RfqStatus): RfqServerTabId | null {
  switch (status) {
    case 'requested':
    case 'quoting':
      return 'requested'
    case 'quoted':
    case 'accepted':
    case 'declined':
      return 'quoted'
    case 'expired':
      return 'expired'
    default:
      return null // 'draft'
  }
}

/** UNCHANGED body by UID-113/ADR-0049 — only the record type narrows to
 * `RfqServerTabId`. Existing tests stay green verbatim: groups has exactly
 * the 3 server keys, drafts are dropped entirely. */
export function groupByTab(rfqs: readonly Rfq[]): Record<RfqServerTabId, Rfq[]> {
  const groups: Record<RfqServerTabId, Rfq[]> = { requested: [], quoted: [], expired: [] }
  for (const rfq of rfqs) {
    const tab = tabForStatus(rfq.status)
    if (tab) groups[tab].push(rfq)
  }
  return groups
}

// ---------------------------------------------------------------------------
// UID-603 (ADR-0176) — `GET /rfq`'s cursor-page envelope + `statusCounts`.
// ---------------------------------------------------------------------------

/** UID-603 (ADR-0176 D6 row 2) — the RFQ list's page size. */
export const RFQ_LIST_PAGE_SIZE = 50

/**
 * UID-603 (ADR-0176 D5) — account-framed, UNFILTERED (except `abandoned_at`),
 * page-independent per-status row counts, straight off the generated
 * contract. Every `RfqStatus` key is PRESENT on the wire: the server
 * zero-fills the aggregate (`rfq-list-cursor.ts`'s `zeroFilledStatusCounts`)
 * precisely so the consumers that fold and sum subsets of it never have to
 * write `?? 0` — five places to forget it in exactly the way D5 exists to
 * stop.
 *
 * The whole FIELD is still optional on the response (issued only when
 * `cursor` is absent, D4/D5) — that optionality lives on
 * `RfqListResponse.statusCounts`, not on the individual keys.
 */
export type RfqStatusCounts = components['schemas']['RfqStatusCounts']

/**
 * The INPUT form the two folds below accept: a possibly-sparse status→count
 * map. Deliberately wider than the wire type — a caller (notably a test
 * fixture) may express "one requested, one quoted" without spelling out five
 * zeroes, and folding a sparse map is well-defined (absent ⟺ zero). The wire
 * type is assignable to this, so production code passes `RfqStatusCounts`
 * directly.
 */
export type PartialRfqStatusCounts = Readonly<Partial<Record<RfqStatus, number>>>

/**
 * UID-603 (ADR-0176 D1.1/D1.3/D5) — `GET /rfq`'s cursor-page envelope,
 * `{ items, nextCursor, totalCount, statusCounts }`, from the GENERATED
 * contract (`apps/api/openapi/schemas/rfq.yaml`'s `RfqListPage`). Aliased
 * here rather than imported as `components['schemas']['RfqListPage']` at
 * every call site, and named `…Response` because `RfqListPage` collides with
 * this vertical's `RfqListPage` COMPONENT.
 *
 * `totalCount` and `statusCounts` are optional on purpose: the server emits
 * both only on a scroll's FIRST request (`cursor` absent — D4/D5), so a
 * consumer must treat their absence on a later page as "unchanged since page
 * 1", never as zero.
 */
export type RfqListResponse = components['schemas']['RfqListPage']

/**
 * The frozen `RfqStatus` set as a VALUE (the generated type is a union, not
 * an enumerable list). Written as a `Record<RfqStatus, true>` rather than an
 * array literal so that adding a status to the contract without adding it
 * here is a COMPILE ERROR — an array would silently under-enumerate, and
 * `serverStatusesForTab` below derives the server's tab→status filter from
 * this list.
 */
const RFQ_STATUS_MEMBERS: Record<RfqStatus, true> = {
  draft: true,
  requested: true,
  quoting: true,
  quoted: true,
  accepted: true,
  declined: true,
  expired: true,
}

export const RFQ_STATUSES = Object.keys(RFQ_STATUS_MEMBERS) as readonly RfqStatus[]

/**
 * UID-603 (ADR-0176 D5) — the statuses a server tab is made of, i.e. the
 * value(s) to send as `GET /rfq`'s REPEATABLE `?status=` filter for that tab.
 *
 * This is the INVERSE of `tabForStatus`, and it is DERIVED from it rather
 * than hand-written, per D5's "reuse that mapping; do not write a second
 * one". Deriving matters more than it looks: a tab is NOT one status —
 * "Requested" is `requested`+`quoting` and "Quoted" is
 * `quoted`+`accepted`+`declined` — so sending the tab id itself as a single
 * `?status=` value (it happens to be a valid `RfqStatus` string for two of
 * the three tabs) would silently drop `quoting`, `accepted` and `declined`
 * rows from their own tabs. That is exactly why the contract made `status` a
 * SET rather than a single value.
 */
export function serverStatusesForTab(tab: RfqServerTabId): readonly RfqStatus[] {
  return RFQ_STATUSES.filter((status) => tabForStatus(status) === tab)
}

/**
 * ADR-0176 D5 — "the nav badge = sum of all non-`draft` entries." Shared by
 * `useHydrateRfqCount` (the sidebar badge) and `RfqListPage` (which pushes
 * the same number into the SAME `useRfqCount` context to skip that hook's
 * own redundant fetch on this route, per its doc comment) — one derivation,
 * never two, so the two can't drift.
 */
export function nonDraftRfqCount(statusCounts: PartialRfqStatusCounts): number {
  let total = 0
  for (const [status, count] of Object.entries(statusCounts) as [RfqStatus, number][]) {
    if (status !== 'draft') total += count
  }
  return total
}

/**
 * ADR-0176 D5 — "the four tab badges = `statusCounts` folded through the
 * EXISTING `tabForStatus` mapping. Reuse that mapping; do not write a second
 * one." Also backs default-tab selection and the tab-strip's visibility gate
 * (`nonDraftRfqCount(statusCounts) > 0`), both of which must read
 * `statusCounts`, never a loaded page's `.length` (D6 row 2).
 */
export function tabBadgeCountsFromStatusCounts(
  statusCounts: PartialRfqStatusCounts,
): Record<RfqServerTabId, number> {
  const totals: Record<RfqServerTabId, number> = { requested: 0, quoted: 0, expired: 0 }
  for (const [status, count] of Object.entries(statusCounts) as [RfqStatus, number][]) {
    const tab = tabForStatus(status)
    if (tab) totals[tab] += count
  }
  return totals
}

/**
 * ADR-0101 (UID-309, product-authorized 2026-08-25) SUPERSEDES ADR-0049's
 * conditional-visibility rule: the Draft tab is now ALWAYS rendered,
 * matching the reference `.dc.html`'s always-4-tabs strip (Draft shows a
 * "0" badge, exactly like the other three, when no draft is queued) rather
 * than disappearing entirely. The `_hasDraftValue` parameter is kept
 * (unused) so call sites — which still gate the DEFAULT active tab pick via
 * `draftPresent` (now `countOpenDrafts(slots) > 0`, ADR-0172 D4 as corrected
 * by code review C1 — NOT a bare `slots.length > 0`, which counted zero-item
 * slots the Draft tab renders no card for) — need no signature churn.
 */
export function visibleRfqTabs(
  _hasDraftValue: boolean,
): readonly { readonly id: RfqTabId; readonly label: string }[] {
  return RFQ_TABS
}

export interface RfqDraftSummary {
  readonly partCount: number
  readonly partNames: readonly string[]
  readonly needByDate: string | null // '' / whitespace normalized to null for the banner
}

/**
 * Bundle-driven describer for the Draft tab's banner (ADR-0049). Takes a
 * structural minimum (`{ name }`) so it needs no `lib/` import and stays
 * trivially testable; a `lib` `RfqBundleItem` is assignable. Shows up to 3
 * part names verbatim — the caller renders "+N more" beyond that (this
 * helper only reports the full count and the full name list; truncating
 * the DISPLAY is the component's job, per the Gate 2 spec's "up to 3 names,
 * then '+N more'").
 */
export function summarizeDraft(
  parts: readonly { readonly name: string }[],
  needByDate: string,
): RfqDraftSummary {
  return {
    partCount: parts.length,
    partNames: parts.slice(0, 3).map((part) => part.name),
    needByDate: needByDate.trim() === '' ? null : needByDate,
  }
}

// ---------------------------------------------------------------------------
// Status pill (components.md "Chip / pill / tag")
// ---------------------------------------------------------------------------

export const RFQ_STATUS_CHIP: Record<
  RfqStatus,
  { readonly label: string; readonly tone: ChipTone }
> = {
  draft: { label: 'Draft', tone: 'neutral' },
  requested: { label: 'Submitted', tone: 'info' },
  // UID-230 fidelity pass: design's `rfqStatusPill('in-review')` (Fictiv
  // Platform.dc.html ~L6707) explicitly renders "In review" on the
  // warning/orange triple (#F0811E/#FFF3E8/#FFD1A9 -> chip-warning's
  // --color-warning-text on --color-warning-bg, 5.65:1 AA), not info/blue —
  // this app's `quoting` status is the same "In review" label/state. `info`
  // was a color mismatch against the design token; `requested`/"Submitted"
  // above correctly keeps info/blue per the design's own 'submitted' case.
  quoting: { label: 'In review', tone: 'warning' },
  quoted: { label: 'Quote returned', tone: 'success' },
  // UID-289 (ADR-0094 D3): the design refresh's customer-facing taxonomy
  // (`Fictiv Platform.dc.html` `rfqStatusLabel`: returned/in-review/
  // submitted/expired) has NO distinct "Accepted" entry — an accepted RFQ
  // is presented as the same returned/quoted-and-actioned state (it has
  // been checked out), not a separate chip. Display-only remap: the
  // `RfqStatus` enum member and the state machine are UNTOUCHED (still
  // `accepted` internally) — only this client map's label/tone changes, to
  // the SAME pair `quoted` already uses.
  accepted: { label: 'Quote returned', tone: 'success' },
  declined: { label: 'Declined', tone: 'neutral' },
  // UID-327/328 (ADR-0112 D6) — `warning` painted an orange chip the design
  // never specifies for `expired`. Verified at `Fictiv Platform.dc.html:7457`
  // (`rfqStatusPill`): it special-cases only `returned`/`in-review`/
  // `submitted`; every OTHER status (including `expired`) falls through to
  // `this.pill('rgba(0,0,0,.55)','#FAFAFA','#D9D9D9')` — a plain neutral
  // grey, not a warning tone. This app's existing `neutral` chip tone IS that
  // exact design triple: `.chip-neutral` (`ui.css`) resolves
  // `--color-surface-canvas: #fafafa` = `#FAFAFA` exactly, and its text
  // `--color-text-secondary: rgba(0,0,0,0.65)` is slightly darker than the
  // design's `.55` (better contrast, no a11y regression). Do NOT use
  // `slate` — that tone is scoped to the Parts Library (`Chip.tsx`).
  // `quoting` keeps `warning` unchanged — only this one entry moves.
  expired: { label: 'Expired', tone: 'neutral' },
}

// ---------------------------------------------------------------------------
// screens.md §14 / components.md "Status stepper" — Submitted → Sourcing → Quoted
// ---------------------------------------------------------------------------

export type RfqStepId = 'submitted' | 'sourcing' | 'quoted'

export const RFQ_STEPS: readonly { readonly id: RfqStepId; readonly label: string }[] = [
  { id: 'submitted', label: 'Submitted' },
  { id: 'sourcing', label: 'Sourcing' },
  { id: 'quoted', label: 'Quoted' },
]

export type StepState = 'completed' | 'current' | 'upcoming'

export function stepState(stepIndex: number, currentIndex: number | null): StepState {
  if (currentIndex === null) return 'upcoming'
  if (stepIndex < currentIndex) return 'completed'
  if (stepIndex === currentIndex) return 'current'
  return 'upcoming'
}

/**
 * Maps an RFQ status onto the 3-step stepper's current index. `expired` is a
 * dead end, not a step on this ladder (screens.md §14 renders it as its own
 * muted card with a "Re-request quote" CTA instead of a stepper position);
 * `draft` never reaches this screen (see `tabForStatus`).
 */
export function resolveRfqStepIndex(status: RfqStatus): number | null {
  switch (status) {
    case 'requested':
    case 'quoting':
      return 1
    case 'quoted':
    case 'accepted':
    case 'declined':
      return 2
    default:
      return null // 'draft', 'expired'
  }
}

/**
 * UID-193 — the single shared gate for rendering the in-progress
 * Submitted→Sourcing→Quoted stepper: true only while the RFQ is still being
 * actively sourced (`requested`/`quoting`). Extracted from `RfqCard.tsx`'s
 * original inline boolean so `RfqListRow.tsx` can reuse the exact same
 * predicate for its compact list-row stepper (UID-193) rather than forking
 * the condition.
 */
export function showsInProgressStepper(status: RfqStatus): boolean {
  return status === 'requested' || status === 'quoting'
}

// ---------------------------------------------------------------------------
// UID-197 (ADR-0075 §3) — RFQ detail "Requested services" card status tag
// ---------------------------------------------------------------------------

/**
 * Whether a quote has been returned for this RFQ — the sole predicate behind
 * the services-card's right-aligned "Requested" / "Quoted with parts" tag
 * (ADR-0075 §3). Exactly the two `RFQ_STATUS_CHIP` success-tone states, kept
 * as one named, unit-tested home (rather than an inline boolean at the
 * `RfqDetailPage` call site) so the card can stay a pure presentational
 * component that never reads `rfq.status` itself.
 */
export function rfqIsQuoted(status: RfqStatus): boolean {
  return status === 'quoted' || status === 'accepted'
}

// ---------------------------------------------------------------------------
// Line display
// ---------------------------------------------------------------------------

/** e.g. `{ value: 5, uom: { code: 'EA' } }` -> "5 EA". */
export function formatQuantity(quantity: Quantity): string {
  return `${quantity.value} ${quantity.uom.code}`
}

// ---------------------------------------------------------------------------
// UID-54 — Mark-Ready + shareable-session handoff (Act 3)
// ---------------------------------------------------------------------------

/**
 * A `SharedSession` is treated as expired if the server already says so, OR
 * if its `expiresAt` has passed the client's clock — belt-and-suspenders
 * against a session that went stale between the last fetch and now (UX spec
 * §1.2 sub-state C: "SharedSession.status === 'expired', i.e. expiresAt has
 * passed" treats the two as the same condition). `expiresAt` is nullable on
 * the schema; a session with no expiry is never considered expired on that
 * basis alone.
 */
export function isSharedSessionExpired(session: SharedSession): boolean {
  if (session.status === 'expired') return true
  if (!session.expiresAt) return false
  return new Date(session.expiresAt).getTime() <= Date.now()
}

/** Short, human-scannable fallback id fragment for the handoff card's
 * generated title when `SharedSession.title` is `null` (UX spec §2.2 point
 * 2: `"RFQ {rfq.id-suffix} — ready to share"`). */
export function rfqIdSuffix(rfqId: string): string {
  return rfqId.slice(-8).toUpperCase()
}

// ---------------------------------------------------------------------------
// UID-118 — per-RFQ ETA chip (Option B: frontend-computed from the real,
// per-RFQ `rfq.createdAt`, NOT a hardcoded placeholder)
// ---------------------------------------------------------------------------

/**
 * Upper bound of the pre-quote turnaround precedent already shown elsewhere
 * on this screen — `RequestSummaryRail.tsx`'s "Typically returns pricing
 * within 1–2 business days." callout. Reused verbatim (N=2, the upper bound
 * of that existing "1–2 business days" range) rather than inventing a new
 * number for the per-RFQ ETA chip.
 */
export const PRE_QUOTE_TURNAROUND_DAYS = 2

/**
 * Computed "estimated return" date for a `requested`/`quoting` RFQ card
 * (UID-118, Option B). Mirrors the backend's calendar-day-math pattern
 * (`apps/api/src/modules/rfq/domain.ts`'s `committedDateForVariant`):
 * `base + N days` in milliseconds, formatted `YYYY-MM-DD` via
 * `.toISOString().slice(0, 10)` — locale-safe, deliberately not
 * `toLocaleDateString`. Base date is `new Date(anchor)` — the estimate is
 * anchored to a timestamp ON THE RFQ, never to the caller's clock. Gate-5 M2:
 * the call site passes `submittedAt ?? createdAt` (see `RfqCard`'s comment for
 * why that fallback is not the one ADR-0093 D5 forbids); this function stays
 * agnostic about which field it was handed.
 *
 * ACCEPTED TRADE-OFF (documented in-code decision, not an ADR): this is
 * plain calendar-day math, not a business-day calculator, so the computed
 * date can land on a weekend. That is acceptable here because the chip is a
 * SOFT, non-binding, never-persisted "estimated return" hint — not a
 * committed delivery promise — and a real business-day calculator would be
 * disproportionate build for a P2 polish ticket. (The same imprecision
 * already exists, uncontested, in `RequestSummaryRail.tsx`'s "1–2 business
 * days" copy this constant reuses.)
 *
 * Returns `null` when the anchor is missing (empty string) or unparseable, so
 * the caller can simply skip rendering the chip instead of showing an
 * "Invalid Date" string.
 */
export function etaForRfq(anchor: string): string | null {
  const base = new Date(anchor)
  if (Number.isNaN(base.getTime())) return null
  const eta = new Date(base.getTime() + PRE_QUOTE_TURNAROUND_DAYS * 24 * 60 * 60 * 1000)
  return eta.toISOString().slice(0, 10)
}

// ---------------------------------------------------------------------------
// UID-134 (ADR-0055) — list-card quote-derived summary fields
// ---------------------------------------------------------------------------
//
// The RFQ row HEADER renders from `rfq` alone; the Quote (variants/prices/lead
// times) is fetched lazily for quoted/accepted RFQs. Ship-by and best-option
// are computable ONLY from that Quote, so these helpers read the SAME quote the
// QuotedLinesPanel already loads (no new price/date contract surface) and their
// call sites are gated to quoted/accepted. They reuse the money-safe
// `applyVariantRatio` / `committedDateForVariant` from `variant-pricing.ts`
// (the web twin of the backend derivation) so the row can never show a number
// derived a second, divergent way. All three are DISPLAY-ONLY — nothing here
// posts to `/cart/lines`; cart-add stays entirely inside
// `QuotedLinesPanel.handleAddToCart` (the UID-126 boundary, ADR-0051 addendum).

/** The lowest-ratio (cheapest) variant on a quote — the "best option" the row
 * summarizes — or `undefined` when the quote carries no variants. Ratio
 * ordering only needs a numeric compare (not money-exactness), but the ratios
 * are decimal strings, so compare their `Number()` values. */
export function bestVariantForQuote(quote: Quote): QuoteVariant | undefined {
  const variants = quote.variants
  if (!variants || variants.length === 0) return undefined
  return variants.reduce((best, v) => (Number(v.ratio) < Number(best.ratio) ? v : best))
}

/** Whether the quote offers a genuine CHOICE of pricing regions (2+ variants) —
 * the sole condition for showing the lowercase "best option" qualifier on the
 * row price (a single- or zero-variant quote shows a plain price). */
export function quoteHasMultipleVariants(quote: Quote): boolean {
  return (quote.variants?.length ?? 0) > 1
}

/** The unit price to show on the row: the first ready priced line scaled by the
 * best (cheapest) variant's ratio, or the unscaled base price when the quote
 * has no variants. `null` when no ready priced line exists to derive from.
 * DISPLAY-ONLY (UID-126 boundary): this value is never posted anywhere. */
export function bestOptionPriceForQuote(
  quote: Quote,
): { readonly amount: string; readonly currency: string } | null {
  const base = quote.lines.find((line) => line.status === 'ready' && line.price)?.price
  if (!base) return null
  const best = bestVariantForQuote(quote)
  const amount = best ? applyVariantRatio(base.amount, best.ratio) : base.amount
  return { amount, currency: base.currency }
}

/** The row's "Ship by" date — DISTINCT from `etaForRfq` (that is the pre-quote
 * turnaround hint for requested/quoting RFQs; this is the post-quote committed
 * ship date). Computed as `committedDateForVariant(quote.createdAt, best
 * variant)` — anchored to when the quote was created, using the cheapest
 * variant's conservative upper-bound lead time — mirroring the backend's
 * cart-commit recompute. Returns `null` (caller renders "Ship by N/A") when the
 * quote carries no variants or `createdAt` is missing/unparseable, never an
 * "Invalid Date". */
export function shipByForQuote(quote: Quote): string | null {
  const best = bestVariantForQuote(quote)
  if (!best) return null
  const base = new Date(quote.createdAt ?? '')
  if (Number.isNaN(base.getTime())) return null
  return committedDateForVariant(base, best)
}

// ---------------------------------------------------------------------------
// UID-134 (ADR-0055) / UID-194 (ADR-0069) / UID-198 (ADR-0074) — the
// "Bundled" explanation, now ONE canonical client-side constant
// ---------------------------------------------------------------------------

/**
 * The bundle-note copy shown on every "bundled" surface across the RFQ
 * vertical — the detail-page header `BundledBadge` disclosure popover
 * (`RfqCard.tsx`), the list-row badge popover (`RfqListRow.tsx`), and the
 * always-visible detail-page callout (`RfqBundledCallout.tsx`, UID-198). A
 * CLIENT-SIDE CONSTANT, deliberately NOT a wire field (the ADR-0053
 * precedent for the shipping copy): the `bundled` flag alone crosses the
 * contract; the human-facing explanation lives here so it can be reworded
 * without a contract change and carries no free-text render risk from the
 * server.
 *
 * UID-198 (ADR-0074) converged all three surfaces onto this single
 * constant, retiring the two divergent predecessors: `BUNDLED_NOTE`
 * (ADR-0055/UID-134, the header popover's original "why" wording, which
 * omitted how to buy a part on its own) and `RFQ_LIST_BUNDLED_NOTE`
 * (ADR-0069/UID-194, which restored that actionable guidance for the list
 * popover). ADR-0074 blessed the fuller UID-194 wording as canonical and
 * repointed the header popover onto it too — a same-page consistency fix,
 * since the detail page now renders both the header badge and the
 * always-visible callout at once, and two differently-worded explanations
 * of the same fact on one screen is a trust defect, not a stylistic choice.
 * See ADR-0074 for the full rationale; there is no remaining per-surface
 * copy fork to reintroduce here.
 */
export const RFQ_BUNDLED_NOTE =
  'These parts are quoted together as one bundle, so the price and lead ' +
  'time reflect the group. To buy a part on its own, submit a new RFQ for ' +
  'just that part.'

// ---------------------------------------------------------------------------
// UID-260 gap #1/#2 (frontend-only) — the RFQ draft's PRE-QUOTE "Choose a quote
// option" preview (`RfqPreQuotePricingPreview.tsx`) and the per-row price-range
// cell (`PartsReviewTable.tsx`) reuse the SAME canonical 3-variant ratio table
// the real post-quote `QuoteVariantSelector` reads (ADR-0051).
//
// UID-292 — this is no longer a hand-mirrored copy of the api's array: both
// sides now re-export the ONE canonical `RFQ_QUOTE_VARIANTS` from
// `@misumi/shared` (`rfq-quote-variants`). It is re-exported here under the same
// name — and typed as the generated `QuoteVariant` (structurally identical to
// the shared interface) — so every consumer keeps its `./domain` import and its
// contract type unchanged. There is now a codegen/import link; the two can no
// longer drift.
// ---------------------------------------------------------------------------

export const RFQ_QUOTE_VARIANTS: readonly QuoteVariant[] = SHARED_RFQ_QUOTE_VARIANTS

// ---------------------------------------------------------------------------
// UID-289 (ADR-0093 content-rendering fields) — human RFQ number, per-line
// part identity, and the Submitted/Quoted meta line. Pure derivations only
// (no `lib/fixtures` import — that stays a component-layer concern, same
// posture as `formatQuantity` above) so every one of these is trivially
// unit-testable and reusable from both the list row and the detail header.
// ---------------------------------------------------------------------------

/** The human, sequential RFQ number (ADR-0093 §C1) when the server derived
 * one, falling back to the PRE-ADR-0093 UUID-fragment format the header used
 * before this field existed — never a raw, unformatted UUID. */
export function rfqNumberDisplay(rfq: {
  readonly id: string
  readonly rfqNumber?: string | null
}): string {
  return rfq.rfqNumber ?? `RFQ-${rfq.id.slice(-6).toUpperCase()}`
}

/** The part-number caption for a quoted-line row (ADR-0093 §C2, amended by
 * ADR-0115 D3, amended by UID-606). No quantity (the row has its own Qty
 * column); no process (moved to the Requirements column).
 *
 * UID-606: `undefined` when the part has no part number, so custom parts
 * render no caption row instead of a bare "—" — reverses D3's "always a
 * dash" rule for this one case. */
export function partCaption(part: RfqLinePartView | undefined): string | undefined {
  return part?.partNumber ?? undefined
}

/** UID-260 (ADR-0085 D1) / ADR-0115 D3 — the material label for the
 * Requirements column's optional `· material` suffix. `SpecSelection
 * .attributes` (`schema.gen.ts:3351-3353`) is a free-form
 * `{[key: string]: string}` bag — the RFQ builder's Configure step
 * (`ConfigurePartsModal.tsx`) writes the human-facing LABEL as the key
 * (e.g. `attributes['Material']`). Per ADR-0111 D7's rule for a free-form
 * bag with more than zero consumers ("one normalizer, never per-consumer
 * key guessing"), every accepted key spelling is listed HERE, in this one
 * accessor — `RequirementsCell` and every other consumer read `attributes`
 * only through this function, never with an inline key lookup.
 *
 * Returns `null` when absent — never fabricated, and never derived by
 * parsing the free-text `requirements` snapshot (a different, unstructured
 * field; manufacturing a structured claim out of prose is exactly what
 * ADR-0115 D3 rules out). Render-length capped (defense-in-depth,
 * ADR-0115 D3's security note): `attributes` values are client-authored,
 * user-influenced strings with no `maxLength` on the wire — the cap runs
 * AFTER `trim()` (below), so a capped value can legitimately end mid-word;
 * accepted, since the alternative (word-boundary-aware truncation) is
 * complexity this defense-in-depth guard doesn't need.
 *
 * Gate 5 S7 fix — iterates the DECLARED key list, not the bag: the
 * previous version iterated `Object.entries(attributes)` and returned the
 * FIRST matching key it happened to encounter, so with both `Material` and
 * `Material Type` present on the same bag, the winner depended on the
 * object's own (insertion-order-derived) key order — non-deterministic
 * from this accessor's point of view. Iterating the declared list instead
 * makes `MATERIAL_ATTRIBUTE_KEYS`'s order the SAME thing as "declared
 * priority," which is what "one declared home for the spellings" should
 * mean. */
const MATERIAL_ATTRIBUTE_KEYS: readonly string[] = ['material', 'material type']
const MATERIAL_LABEL_MAX_LENGTH = 64

export function materialLabelForLine(
  specSelection: RfqLineView['specSelection'] | null | undefined,
): string | null {
  const attributes = specSelection?.attributes
  if (!attributes) return null
  const byNormalizedKey = new Map<string, string>()
  for (const [key, value] of Object.entries(attributes)) {
    if (typeof value !== 'string') continue
    const normalizedKey = key.trim().toLowerCase()
    // First-write-wins per normalized key — a duplicate-cased key (e.g.
    // both `Material` and `material` on the same bag) resolves the same
    // way regardless of the bag's own enumeration order.
    if (!byNormalizedKey.has(normalizedKey)) byNormalizedKey.set(normalizedKey, value)
  }
  for (const key of MATERIAL_ATTRIBUTE_KEYS) {
    const value = byNormalizedKey.get(key)
    if (!value) continue
    const trimmed = value.trim()
    if (!trimmed) continue
    return trimmed.slice(0, MATERIAL_LABEL_MAX_LENGTH)
  }
  return null
}

/** `1` -> `'unit'`, everything else -> `'units'` — the design's own
 * pluralization convention for this meta line (`dc.html:9842`'s
 * `partCountText` pluralizes; `9873`'s `metaLabel` string literally does
 * not, which UID-328/ADR-0112 D4 treats as a copy defect, not a spec to
 * copy — see `rfqPartsMetaLine`'s doc comment). */
function unitWord(quantity: number): string {
  return quantity === 1 ? 'unit' : 'units'
}

/** The shared segment list behind both `rfqPartsMetaLine` and
 * `rfqPartsMetaLineBody` below (UID-328/ADR-0112 D4) — every segment is
 * present-or-omitted independently, then joined with `' · '` by the two
 * exported wrappers. `includeCount` toggles ONLY the leading "N parts"/"1 part" segment for the
 * multi-line case — the single-line case has no such segment to toggle (the
 * list row's own bold `partCountLabel` already covers it structurally, not
 * textually, for N===1 too). */
function rfqPartsMetaSegments(
  lines: readonly RfqLineView[],
  includeCount: boolean,
): readonly string[] {
  const [first, ...rest] = lines
  if (!first) return []
  if (rest.length === 0) {
    const quantity = first.quantity.value
    return [
      first.part?.process ?? null,
      `${quantity} ${unitWord(quantity)}`,
      first.part?.partNumber ?? null,
    ].filter((segment): segment is string => Boolean(segment))
  }
  const totalQty = lines.reduce((sum, line) => sum + line.quantity.value, 0)
  return [
    includeCount ? `${lines.length} parts` : null,
    `${totalQty} ${unitWord(totalQty)} total`,
    first.part?.partNumber ?? null,
  ].filter((segment): segment is string => Boolean(segment))
}

/**
 * UID-328/ADR-0112 D4 — the RFQ-level parts meta line, DESIGN-LITERAL
 * (keeps the leading count segment): for a single-line RFQ,
 * `"{process} · {qty} {unit}[s] · {partNumber}"`; for a multi-line RFQ,
 * `"{N} parts · {totalQty} units total · {partNumber}"` (mirrors the
 * design's `metaLabel` derivation, `Fictiv Platform.dc.html` ~L9873 — down
 * to ending on the FIRST line's part number, which the pre-ADR-0112 build
 * dropped, and saying "units" rather than the raw UOM code, which
 * `formatQuantity`'s Qty-column usage is UNCHANGED and keeps). Every segment
 * is omitted independently when absent (no seeded `part`, e.g. a
 * catalog-sourced line).
 *
 * Two DELIBERATE deviations from the literal design, both flagged (per the
 * ADR): the design's own multi-line `metaLabel` counts the parts a SECOND
 * time when concatenated after `partCountText` (`dc.html:3481`) — this
 * helper does not repeat that duplication internally, but IS still meant to
 * be prefixed by the caller's own bold count (see `RfqDetailPage.tsx`'s
 * unprefixed usage vs. `RfqListRow`'s prefixed one, and `rfqPartsMetaLineBody`
 * below for the latter). And the design never pluralizes ("1 units");
 * `unitWord` above fixes that copy defect.
 *
 * Sole consumer of the COUNT-BEARING form: `RfqDetailPage.tsx`'s subheading,
 * which joins this with no separate bold count prefix (`dc.html:3514`) — so
 * this export's count segment is load-bearing there, unlike the list row.
 */
export function rfqPartsMetaLine(lines: readonly RfqLineView[]): string {
  return rfqPartsMetaSegments(lines, true).join(' · ')
}

/**
 * UID-328/ADR-0112 D4 — the list row's variant of `rfqPartsMetaLine` above,
 * WITHOUT the leading "N parts" count segment: `RfqListRow.tsx` already
 * renders `<strong>{partCountLabel}</strong>` immediately before this string
 * (`dc.html:3481`'s `<b>{{ r.partCountText }}</b> · {{ r.metaLabel }}`), so
 * printing the count a second time here produced the reported "2 parts · 2
 * parts · 2 EA total" defect (UID-328 AC2). Identical to
 * `rfqPartsMetaLine` for the single-line case, which never had a count
 * segment to drop in the first place.
 */
export function rfqPartsMetaLineBody(lines: readonly RfqLineView[]): string {
  return rfqPartsMetaSegments(lines, false).join(' · ')
}

/** The "Submitted {date} · Quoted {date}" recap line (ADR-0093 §C4) — a
 * `Quoted` segment is included only once the RFQ actually carries a
 * `quotedAt` (nullable until the quote returns); `Submitted` is included
 * only when `createdAt` is present (defensive — every real RFQ has one). */
export function rfqSubmittedQuotedLabel(rfq: {
  readonly createdAt?: string | null
  readonly quotedAt?: string | null
}): string {
  const segments: string[] = []
  if (rfq.createdAt) segments.push(`Submitted ${rfq.createdAt.slice(0, 10)}`)
  if (rfq.quotedAt) segments.push(`Quoted ${rfq.quotedAt.slice(0, 10)}`)
  return segments.join(' · ')
}

// ---------------------------------------------------------------------------
// ADR-0095 (UID-302) — Home "Open quotes" row title + subtotal
// ---------------------------------------------------------------------------

/**
 * UID-442 — the one place a single RFQ line's display name is resolved for
 * the Home Open-quotes row title, in the SAME precedence order `RfqCard`
 * and `RfqDetailSummaryRail` already use (`line.part?.name ??
 * displayNameFor(line.sourceable)`), so the Home row can never disagree
 * with the RFQ card the buyer clicks through to.
 *
 * SERVER FIRST: `RfqLineView.part.name` is the real, tenant-parts-library
 * identity resolved server-side and snapshotted onto the line at write time
 * (UID-439 / ADR-0138). It is authoritative — the client fixture set is
 * demo data and cannot know a real part.
 *
 * FIXTURE LAST: `part` being absent is UID-439's deliberate
 * omit-rather-than-fabricate signal that the server resolved no real
 * identity, so the fixture lookup is still the correct LAST RESORT (a demo
 * RFQ built from `apexAutomationGantry` has no server-side library row, and
 * must keep rendering its fixture name). `displayNameFor` itself returns
 * the literal `'Unnamed part'` on a miss, which is why this returns
 * `undefined` on a fixture miss instead of calling it — the caller needs to
 * distinguish "this line has no name" from "this line is named
 * 'Unnamed part'" in order to try the NEXT line.
 */
function lineDisplayName(line: RfqLineView): string | undefined {
  const serverName = line.part?.name
  if (serverName) return serverName
  return findSourceableById(line.sourceable.id)?.name !== undefined
    ? displayNameFor(line.sourceable)
    : undefined
}

/**
 * The Home Open-quotes row's title (ADR-0095 D1). There is no name/title
 * field on `Rfq`/`RfqView` — this derives one from the RFQ's own lines via
 * {@link lineDisplayName}, the same server-first/fixture-last precedence
 * every other RFQ surface resolves line names through, rather than
 * inventing a second naming scheme.
 *
 * Named from the FIRST line that actually resolves to a real `name` — not
 * strictly the first line — so one unknown/unnamed line does not blank out
 * an otherwise-nameable RFQ. Falls back to the literal "Unnamed part" only
 * when NO line resolves through EITHER path (UID-442: this used to be the
 * outcome for essentially every real RFQ, because the only path consulted
 * was the static client fixture map, which a server-generated
 * `sourceable.id` is never in). `displayNameFor`'s existing " (SKU)"
 * parenthetical is preserved verbatim on the fixture path.
 *
 * The "+N more" suffix counts ALL lines (not just nameable ones) — it means
 * "and N more lines on this RFQ", not "and N more nameable lines" — and is
 * omitted entirely for a single-line RFQ.
 */
export function rfqRowTitle(rfq: Rfq): string {
  // The `as readonly RfqLineView[]` cast is required, not incidental:
  // openapi-typescript infers `Rfq['lines']`'s element type as an
  // intersection of the frozen `Rfq.lines: LineItem[]` and the additive
  // `RfqView.lines?: RfqLineView[]`, which does not expose `part` through
  // plain property access — see the `RfqLineView` doc comment above, and
  // the identical cast in `RfqCard.tsx`/`RfqDetailSummaryRail.tsx`. Safe
  // because the server always emits `RfqView` (never the bare frozen `Rfq`)
  // for both `GET /rfq` and `GET /rfq/{id}` — one `toWireRfq` projection
  // feeds both, so the list carries `part` exactly as the detail does.
  const lines = rfq.lines as readonly RfqLineView[]
  const name = lines.map(lineDisplayName).find((candidate) => candidate !== undefined)
  const extraLines = lines.length - 1
  const title = name ?? 'Unnamed part'
  return extraLines > 0 ? `${title} +${extraLines} more` : title
}

/**
 * UID-302 fix (ADR-0095 D3 amendment, 2026-08-25) — the SINGLE shared gate
 * for whether a quote line may feed ANY customer-facing money figure derived
 * from quote lines. A `'ready'` line can still lack a `deliveryPromise`
 * (both `price` and `deliveryPromise` are independently-optional on
 * `LineItem` — see the `OrderEligibleLineItem` discriminated-union comment
 * in `@misumi/shared`'s `line-item.ts`), and per UID-230 Item 1 such a line
 * must NOT inflate a money figure a customer can act on: it can never
 * actually be added to cart (the real `/cart/lines` POST requires both
 * fields), so counting it makes the displayed figure unreachable.
 *
 * This consolidates what were THREE independently-hand-written copies of the
 * exact same three-clause boolean into one export:
 *   - `RfqCard.tsx`'s private `isRowAddable` (the original UID-230 Item 1
 *     fix, Gate 5 review W2 finding — five call sites there),
 *   - `RfqDetailSummaryRail.tsx`'s inlined `readyLines` filter (the Total /
 *     Quantity / Add-to-cart-button gate), and
 *   - this module's own `subtotalForQuote`, which — before this fix — used
 *     the LOOSER `ready && price` predicate (mirroring the display-only
 *     `bestOptionPriceForQuote` instead), so Home's Subtotal could read
 *     HIGHER than the RFQ detail page's Total for the SAME quote. See the
 *     ADR-0095 amendment (2026-08-25) for the full history and the human's
 *     ruling to fix rather than accept the divergence.
 *
 * Deliberately NOT applied to `bestOptionPriceForQuote` (`domain.ts` above) —
 * that function returns a PER-UNIT display price, not a summed money figure
 * a customer could compare against a checkout total, so it stays out of
 * scope for this fix. Left as-is CONSCIOUSLY, not missed.
 */
export function isQuoteLineAddable(line: RfqLineItem): boolean {
  return line.status === 'ready' && Boolean(line.price) && Boolean(line.deliveryPromise)
}

/**
 * The pricing-region variant in effect for a quote: the caller's explicit
 * selection, else the FIRST variant.
 *
 * UID-116 (ADR-0051) / UID-238 item 2 established that the fallback happens at
 * the POINT OF USE — never by seeding selection state in an effect, which is
 * what let RFQ A's pick leak into RFQ B. That rule is unchanged; this only
 * gives the resolution ONE implementation.
 *
 * UID-325 (Gate 5) extracted it. Three surfaces resolve the effective variant —
 * `QuoteOptionsCard` (the selector), `QuotedLinesPanel` (per-row prices) and
 * `RfqDetailSummaryRail` (Region/Total/lead time, and what Checkout actually
 * submits) — and they had three copies of `selectedVariantId ?? variants[0]?.id`
 * held together by comments saying "the same resolution as". A divergence here
 * would show the customer one region's price and charge them another's, so it
 * is exactly the kind of agreement that should be structural rather than
 * commented. Mirrors how UID-302 consolidated `isQuoteLineAddable` above.
 *
 * Returns `undefined` when the quote carries no variants (a pre-UID-116 quote),
 * which every caller already treats as "render no variant UI".
 */
export function resolveQuoteVariantId(
  quote: Pick<Quote, 'variants'> | undefined,
  selectedVariantId: QuoteVariant['id'] | undefined,
): QuoteVariant['id'] | undefined {
  return selectedVariantId ?? quote?.variants?.[0]?.id
}

/**
 * The Home Open-quotes row's "Subtotal" (ADR-0095 D3) — a REAL, decimal-safe
 * sum, not the per-unit `bestOptionPriceForQuote` price. A quote line IS a
 * `LineItem`, carrying both `quantity` and `price`, so no join back to
 * `rfq.lines` is needed.
 *
 * Considers only lines that pass `isQuoteLineAddable` — the SAME
 * `deliveryPromise`-gated predicate the two checkout-adjacent surfaces
 * (`RfqCard.tsx`, `RfqDetailSummaryRail.tsx`) use, per the UID-302 fix
 * (ADR-0095 amendment, 2026-08-25; see that function's doc comment). Per
 * line: `multiplyAmount(applyVariantRatio(line.price.amount, best.ratio),
 * line.quantity.value)`, where `best = bestVariantForQuote(quote)`; the
 * ratio step is skipped when the quote has no variants. Summed via
 * `addAmounts` — integer cents throughout (ADR-0004); never a bare `*`/`+`
 * on a decimal string, never a float accumulator.
 *
 * Returns `null` (caller renders "N/A") when there is no addable line, or
 * when the addable lines carry MIXED CURRENCIES — a confidently wrong money
 * figure is worse than "N/A" on a landing dashboard.
 *
 * DELIBERATELY EXCLUDES `quote.charges`: ADR-0029's rule is
 * `total = Σ(line.price×qty) + Σ(charge.amount)`. Folding charges in here
 * would make this a *total*, not the "Subtotal" the design labels it — do
 * not "fix" this upward to include them.
 *
 * DISPLAY-ONLY — nothing here posts anywhere (the UID-126 boundary).
 *
 * FORMERLY a KNOWN DIVERGENCE from the two checkout-adjacent siblings
 * (ADR-0095 G9, code review 2026-08-25): this function used to filter on the
 * looser `ready && price` predicate (mirroring the display-only
 * `bestOptionPriceForQuote`), so Home's Subtotal could read HIGHER than the
 * RFQ detail page's Total for the same quote. RESOLVED by the UID-302 fix —
 * see the ADR-0095 amendment and `isQuoteLineAddable`'s doc comment. This
 * function still resolves against the CHEAPEST variant rather than the
 * selected one (mirroring `bestOptionPriceForQuote`'s intent for Home) —
 * that part of D3 is unchanged and still deliberate.
 */
export function subtotalForQuote(
  quote: Quote,
): { readonly amount: string; readonly currency: string } | null {
  const readyPricedLines = quote.lines.filter(
    (line): line is RfqLineItem & { price: NonNullable<RfqLineItem['price']> } =>
      isQuoteLineAddable(line),
  )
  const [firstLine, ...restLines] = readyPricedLines
  if (!firstLine) return null

  const currency = firstLine.price.currency
  if (restLines.some((line) => line.price.currency !== currency)) return null

  const best = bestVariantForQuote(quote)
  const lineSubtotals = readyPricedLines.map((line) => {
    const unitAmount = best ? applyVariantRatio(line.price.amount, best.ratio) : line.price.amount
    return multiplyAmount(unitAmount, line.quantity.value)
  })

  return { amount: addAmounts(lineSubtotals), currency }
}
